import { rem } from 'polished'
import React, { useCallback } from 'react'
import { Controller, useController, useFormContext } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import AddressDisplay from './Fields/AddressDisplay'
import {
  priceFieldProps,
  priceRegisterConfig,
  useFieldValidation,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import RHFDatePickerInput from './Fields/RHFDatePickerInput'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import ShowMore from './Fields/ShowMore'
import TimeInput from './Fields/TimeInput'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import useEndPlaceIsSame from './Hooks/useEndPlaceIsSame'
import useEnsureGeoPlace from './Hooks/useEnsureGeoPlace'
import type { CreateCarRentalItemViewModel } from '../Create/models'
import type { PatchCarRentalItemViewModel } from '../Edit/models'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import type {
  TripAddressAutocompleteOption,
} from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import TripAddressAutocomplete from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import type { AutocompletePlaceParam } from 'tripPlanner/types/google'
import {
  ADDRESS_RESPONSE_DETAILS,
} from 'tripPlanner/types/google'
import { convertGoogleAddressComponentsToRegion } from 'tripPlanner/utils'

const StyledVerticalSpacer = styled(VerticalSpacer)`
  margin-top: ${rem(20)};
  margin-bottom: ${rem(20)};
`

const EndPlaceContainer = styled.div`
  padding-bottom: ${rem(8)};
`

const DividerContainer = styled.div`
  height: ${rem(24)};
`

const PriceInput = styled(Input)`
  width: ${rem(100)};
`

const AUTOCOMPLETE_TYPES: AutocompletePlaceParam = []

const START_GEO_DEP_KEYS: Array<string> = ['startPlace.name']
const END_GEO_DEP_KEYS: Array<string> = ['endPlace.name']

interface Props {
  tripId: string
}

function CarFields({ tripId }: Props) {
  const { required, urlRegisterConfig } = useFieldValidation()
  const intl = useIntl()
  const methods = useFormContext<
    CreateCarRentalItemViewModel | PatchCarRentalItemViewModel
  >()
  const { watch, control, setValue, register, clearErrors } = methods

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  useEnsureGeoPlace(methods, 'startPlace', START_GEO_DEP_KEYS)
  useEnsureGeoPlace(methods, 'endPlace', END_GEO_DEP_KEYS)

  const startPlace = watch('startPlace')
  const endPlace = watch('endPlace')

  useEndPlaceIsSame(methods)

  const handleAddressChange = useCallback(
    (
      placeName: 'startPlace' | 'endPlace',
      selectedOption: TripAddressAutocompleteOption,
    ) => {
      setValue(
        `${placeName}.name`,
        selectedOption.details?.name || selectedOption.label,
      )

      setValue(
        `${placeName}.type`,
        selectedOption.prediction ? 'GEO' : 'CUSTOM',
      )

      if (selectedOption.prediction) {
        const { place_id } = selectedOption.prediction
        setValue(`${placeName}.id`, place_id)
      }

      if (selectedOption.details) {
        const { geometry, formatted_address, website, address_components } =
          selectedOption.details

        if (address_components) {
          const region =
            convertGoogleAddressComponentsToRegion(address_components)
          if (region) {
            setValue(`${placeName}.region`, region)
          }
        }

        if (website) {
          setValue('url', website)
        }

        formatted_address && setValue(`${placeName}.address`, formatted_address)

        if (geometry?.location) {
          setValue(`${placeName}.point.lat`, geometry.location.lat)
          setValue(`${placeName}.point.lng`, geometry.location.lng)
        }
      }
    },
    [setValue],
  )

  const handleStartAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('startPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const handleEndAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('endPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const {
    fieldState: { error: endPlaceError },
  } = useController({ name: 'endPlace' })

  const handleSetStartPlaceToEndPlace = useCallback(() => {
    setValue('endPlace', startPlace)

    // React hook form not recognizing valid field after this update
    if (endPlaceError) {
      clearErrors('endPlace')
    }
  }, [setValue, startPlace, clearErrors, endPlaceError])

  const rentalCompanyLabel = intl.formatMessage({
    id: 'Mkxvm6z5Bx',
    defaultMessage: 'Rental company',
    description: 'Form label for rental company name field in car rental trip item editor',
  })

  const rentalCompanyPlaceholder = intl.formatMessage({
    id: '6+Ieo2Ppw2',
    defaultMessage: 'Enter a rental company',
    description: 'Placeholder text for rental company input in car rental trip item editor',
  })

  const pickUpDetailsHeading = intl.formatMessage({
    id: 'CnjlSf7TU4',
    defaultMessage: 'Pick up details',
    description: 'Section heading for pick up details in car rental trip item editor',
  })

  const pickUpLocationLabel = intl.formatMessage({
    id: 'It6gXyCQda',
    defaultMessage: 'Pick up location',
    description: 'Form label for pick up location field in car rental trip item editor',
  })

  const searchLocationPlaceholder = intl.formatMessage({
    id: 'C2pRxS0yAl',
    defaultMessage: 'Search for location',
    description: 'Placeholder text for location search input in car rental trip item editor',
  })

  const addressLabel = intl.formatMessage({
    id: 'FWjAhrGxyI',
    defaultMessage: 'Address',
    description: 'Form field label for address display in car rental trip item',
  })

  const pickUpDateLabel = intl.formatMessage({
    id: '+bfRfX99Us',
    defaultMessage: 'Pick up date',
    description: 'Form label for pick up date field in car rental trip item editor',
  })

  const timeLabel = intl.formatMessage({
    id: 'C93Y88M3uo',
    defaultMessage: 'Time',
    description: 'Form label for time field in car rental trip item editor',
  })

  const selectTimePlaceholder = intl.formatMessage({
    id: 'kRzyMrGTUv',
    defaultMessage: 'Select time',
    description: 'Placeholder text for time picker in car rental trip item editor',
  })

  const dropOffDetailsHeading = intl.formatMessage({
    id: 'dW2HEdRoP2',
    defaultMessage: 'Drop off details',
    description: 'Section heading for drop off details in car rental trip item editor',
  })

  const dropOffLocationLabel = intl.formatMessage({
    id: '0GdwJ7olHT',
    defaultMessage: 'Drop-off location',
    description: 'Form label for drop-off location field in car rental trip item editor',
  })

  const dropOffDateLabel = intl.formatMessage({
    id: 'w5TXhLLuO8',
    defaultMessage: 'Drop-off date',
    description: 'Form label for drop-off date field in car rental trip item editor',
  })

  const priceLabel = intl.formatMessage({
    id: '0HgaFAK41n',
    defaultMessage: 'Price',
    description: 'Form label for price field in car rental trip item editor',
  })

  const websiteLabel = intl.formatMessage({
    id: 'jWpmw/0IVx',
    defaultMessage: 'Website',
    description: 'Form label for website URL field in car rental trip item editor',
  })

  const pricePlaceholder = intl.formatMessage({
    id: '11royVeeZs',
    defaultMessage: '0.00',
    description: 'Placeholder for price input field in the pricing or cost breakdown section',
  })

  const websitePlaceholder = intl.formatMessage({
    id: 'YJDWIzhAMQ',
    defaultMessage: 'https://',
    description: 'Input placeholder text for website URL input field',
  })

  const additionalNotesLabel = intl.formatMessage({
    id: 'ubTjyMVkR4',
    defaultMessage: 'Additional notes',
    description: 'Form label for additional notes field in car rental trip item editor',
  })

  const notesPlaceholder = intl.formatMessage({
    id: 'ZLW6CALtbl',
    defaultMessage: 'Add notes about this car rental',
    description: 'Placeholder text for notes textarea in car rental trip item editor',
  })

  const copyAddressHelpText = <BodyText variant="medium">
    <FormattedMessage
      id="zQU//CIyCQ"
      defaultMessage="Add same address as <link>pick up location</link>"
      description="Link to copy pick up address to drop off address in car rental form"
      values={{
        link: (chunks) => <TextLink onClick={handleSetStartPlaceToEndPlace} underline={false}>{chunks}</TextLink>,
      }}
    />
  </BodyText>

  return (
    <StyledVerticalSpacer gap={16}>
      <Input
        {...register('companyName')}
        label={rentalCompanyLabel}
        placeholder={rentalCompanyPlaceholder}
      />

      <Heading variant="heading4">{pickUpDetailsHeading}</Heading>

      <TripAddressAutocomplete
        {...register('startPlace.name', { required })}
        required
        label={pickUpLocationLabel}
        placeholder={searchLocationPlaceholder}
        initPlaceId={startPlace?.type === 'GEO' ? startPlace.id : undefined}
        autocompleteTypes={AUTOCOMPLETE_TYPES}
        details={ADDRESS_RESPONSE_DETAILS}
        onPlaceSelect={handleStartAddressChange}
        startIcon={<LineMapMarkerIcon />}
      />
      {startPlace?.address && (
        <AddressDisplay title={addressLabel} value={startPlace.address} />
      )}

      <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
        <RHFDatePickerInput
          name="startDate"
          label={pickUpDateLabel}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('startTime')}
          label={timeLabel}
          placeholder={selectTimePlaceholder}
        />
      </Group>

      <DividerContainer>
        <Divider kind="primary" />
      </DividerContainer>

      <Heading variant="heading4">{dropOffDetailsHeading}</Heading>

      <EndPlaceContainer>
        <TripAddressAutocomplete
          {...register('endPlace.name', { required })}
          required
          label={dropOffLocationLabel}
          placeholder={searchLocationPlaceholder}
          initPlaceId={endPlace?.type === 'GEO' ? endPlace.id : undefined}
          autocompleteTypes={AUTOCOMPLETE_TYPES}
          details={ADDRESS_RESPONSE_DETAILS}
          onPlaceSelect={handleEndAddressChange}
          startIcon={<LineMapMarkerIcon />}
          helpText={copyAddressHelpText}
        />
        {endPlaceError && copyAddressHelpText}
      </EndPlaceContainer>

      {endPlace?.address && (
        <AddressDisplay title={addressLabel} value={endPlace.address} />
      )}

      <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
        <RHFDatePickerInput
          name="endDate"
          label={dropOffDateLabel}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('endTime')}
          label={timeLabel}
          placeholder={selectTimePlaceholder}
        />
      </Group>

      <DividerContainer>
        <Divider kind="primary" />
      </DividerContainer>

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />

      <ShowMore>
        <PriceInput
          {...register('price', { ...priceRegisterConfig })}
          label={priceLabel}
          placeholder={pricePlaceholder}
          {...priceFieldProps}
        />

        <Input
          {...register('url', { ...urlRegisterConfig })}
          label={websiteLabel}
          placeholder={websitePlaceholder}
        />

        <TextArea
          {...register('notes')}
          placeholder={notesPlaceholder}
          label={additionalNotesLabel}
          resize="vertical"
          size="tall"
        />
      </ShowMore>
    </StyledVerticalSpacer>
  )
}

export default CarFields
